import { createSlice, current } from "@reduxjs/toolkit";
import Result from "../../pages/schedule/createschedule/formsteps/result";
import ReviewStep from "../../pages/schedule/createschedule/formsteps/reviewstep";
import { api } from "../../api/httpclient";
import i18next from "i18next";
import GeneralStep from "../../pages/schedule/createschedule/formsteps/generalstep";
import SelectReportStep from "../../pages/schedule/createschedule/formsteps/selectreportstep";
import RecurrenceNotificationStep from "../../pages/schedule/createschedule/formsteps/recurrencenotificationstep";
import FilterStep from "../../pages/schedule/createschedule/formsteps/filterstep";
import { AggregateFunction } from "../../lib/aggregatefunctions";
import {
  collectParametersFromRules,
  getAlertCoveredPeriodIntervals,
  getAlertImportances,
  getAlertRecurrsEveryIntervals,
  getAlertVariantCoveredPeriodIntervals,
  getComparisonOperators,
  notificationDurationMiddle,
} from "../../lib/utils";
import { Type } from "ajv/dist/compile/util";
import { formatQuery } from "react-querybuilder";
import { addNotification } from "./notificationsslice";

export const steps = [
  SelectReportStep,
  GeneralStep,
  RecurrenceNotificationStep,
  FilterStep,
  ReviewStep,
  Result,
];

export const stepsWithMultipleDataset = [
  SelectReportStep,
  GeneralStep,
  RecurrenceNotificationStep,
  ReviewStep,
  Result,
];

export const stepsName = [
  { label: "reports.reports", isValid: true },
  { label: "datasets.create.general", isValid: undefined },
  { label: "alerts.recurrence", isValid: undefined },
  { label: "extras.filter", isValid: undefined },
  { label: "datasets.create.review", isValid: undefined },
  { label: "datasets.create.result", isValid: undefined },
];

export const stepsWithMultipleDatasetName = [
  { label: "reports.reports", isValid: true },
  { label: "datasets.create.general", isValid: undefined },
  { label: "alerts.recurrence", isValid: undefined },
  { label: "datasets.create.review", isValid: undefined },
  { label: "datasets.create.result", isValid: undefined },
];

export const timeIntervalsDDL = [
  { Id: 0, Title: i18next.t("slices.none") },
  { Id: 5, Title: i18next.t("slices.fiveMins") },
  { Id: 10, Title: i18next.t("slices.tenMins") },
  { Id: 15, Title: i18next.t("slices.fiveteenMins") },
  { Id: 30, Title: i18next.t("slices.thirtyMins") },
  { Id: 60, Title: i18next.t("slices.oneHour") },
  { Id: 120, Title: i18next.t("slices.twoHours") },
  { Id: 180, Title: i18next.t("slices.threeHours") },
  { Id: 240, Title: i18next.t("slices.fourHours") },
  { Id: 300, Title: i18next.t("slices.fiveHours") },
  { Id: 360, Title: i18next.t("slices.sixHours") },
  { Id: 720, Title: i18next.t("slices.twelveHours") },
  { Id: 1440, Title: i18next.t("slices.oneDay") },
  { Id: 2880, Title: i18next.t("slices.twoDays") },
  { Id: 4320, Title: i18next.t("slices.threeDays") },
  { Id: 10080, Title: i18next.t("slices.sevenDays") },
  { Id: 43200, Title: i18next.t("slices.thirtyDays") },
];

export const maxSize = 20 * 1024 * 1024 * 1024; //20GB

const defaultDataset = {
  Id: 0,
  Title: "None",
  Description: "",
  CategoryId: 0,
  Alerts: [],
  Fields: [],
  Users: [],
  Where: [],
  OrderId: -1,
};

const defaultDatasetId = 0;

const defaultFormInitialValues = {
  //step1:reports&widgets
  rptId: 0,
  reportId: null,
  widgetId: 0,
  Reports: [],
  Widgets: [],
  Datasets: [],
  GoogleList: [],
  Connections: [],
  Where: [],
  WhereObject: "",
  WhereStatement: "",
  WhereParameters: "",
  WhereDate: [],
  ShowGoogleSheets: false,
  DatasetId: defaultDatasetId,
  HasMultipleDatasets: false,
  ConnectorConfigId: 0,
  ConnectionId: null,

  Name: "",
  Description: "",
  Type: null,
  Source: null,
  File: null,
  FilePath: "",
  SheetId: 0,
  GoogleSheetId: 0,
  ColumnNames: [],
  CustomFields: [{ index: 1, field: "", value: "" }],
  SheetName: "",
  CronValue: "",
  RecurrsEvery: "",
  RecurrencyFrom: "",
};

const initialState = {
  pageLoaded: false,
  pageDisposed: true,
  processing: false,
  processingText: "",
  mode: 1,

  currentStepIndex: 0,
  previousStepIndex: -1,
  previousStepValue: "Cancel",
  nextStepIndex: 1,
  nextStepValue: "Next",
  steps: stepsName,
  stepPages: steps,
  stepperOrientation: "horizontal",
  currentDataset: defaultDataset,

  formKey: new Date().toString(),
  formInitialValues: { ...defaultFormInitialValues },
  formValues: { ...defaultFormInitialValues },
  formClientErrors: {},
  formServerErrors: [],

  toolsVisible: true,
  panes: [
    {
      // size: "30%",
      size: "20%",
      min: "20%",
      max: "25%",
      collapsible: false,
    },
    {
      //min: "700px",
      //size:"60%",
      min: "70%",
      max: "80%",
      collapsible: false,
    },
  ],
};

const createSchedulePageSlice = createSlice({
  name: "createSchedulePage",
  initialState: initialState,
  reducers: {
    setInitialState: (state, action) => ({ ...initialState }),

    setProcessing: (state, action) => {
      const { processing = true, processingText = "" } = action.payload;
      return {
        ...state,
        processing: processing,
        processingText: processing == false ? "" : processingText,
      };
    },
    setPanes: (state, action) => ({ ...state, panes: { ...action.payload } }),

    setPageValue: (state, action) => ({
      ...state,
      [action.payload.id]: action.payload.value,
    }),

    setFormValue: (state, action) => {
      let id = action.payload.id;
      let value = action.payload.value;
      let regex = action.payload.regex;
      let regexPattern = null;
      if (regex) {
        const match = regex.match(/^\/(.*?)\/([gimsuy]*)$/);
        if (match) {
          regexPattern = new RegExp(match[1], match[2]);
        }
      }
      return {
        ...state,
        formValues: {
          ...state.formValues,
          [id]: value && regexPattern ? value.replace(regexPattern, "") : value,
        },
      };
    },

    showHideGoogleSheets: (state, action) => {
      return {
        ...state,
        formValues: {
          ...state.formValues,
          ShowGoogleSheets: action.payload,
        },
      };
    },

    pageLoaded: (state, action) => {
      //payload: {reports: , currentUser: ,... }
      const pageState = current(state);

      if (pageState.pageDisposed) return state;

      return {
        ...state,
        pageLoaded: true,
        processing: false,
        processingText: "",
        pageDisposed: false,
        formValues: {
          ...state.formValues,
          Reports: action.payload.Reports,
        },
      };
    },

    disposePage: (state, action) => {
      //payload: bool
      if (action.payload) return { ...initialState };
      else return { ...state, pageDisposed: false };
    },

    setCurrentStepIndex: (state, action) => {
      return {
        ...state,
        currentStepIndex: action.payload,
      };
    },

    setSteps: (state, action) => {
      return {
        ...state,
        steps: action.payload,
      };
    },

    setStepPages: (state, action) => {
      return {
        ...state,
        stepPages: action.payload,
      };
    },

    setNavigation: (state, action) => {
      const {
        previousStepIndex = -1,
        previousStepValue = "Cancel",
        nextStepIndex = 1,
        nextStepValue = "Next",
      } = action.payload;
      return {
        ...state,
        previousStepIndex: previousStepIndex,
        previousStepValue: previousStepValue,
        nextStepIndex: nextStepIndex,
        nextStepValue: nextStepValue,
      };
    },
    datasetLoaded: (state, action) => {
      const pageState = current(state);

      if (pageState.pageDisposed) return state;

      let currentDataset = pageState.formValues.Datasets.find(
        (e) => e.Id == action.payload.datasetId,
      );

      //aggregate expressions - already sorted by title by api
      let aggregateExpressions = action.payload.fields
        .filter(
          (e) =>
            e.IsExpression == true &&
            e.IsAggregate == true &&
            e.DataTypeId == 2,
        )
        .map((item) => ({ Id: item.Id, Title: item.Title, Name: item.Name }));
      //aggregateExpressions = sortArrayByString(aggregateExpressions, "Title");
      aggregateExpressions = [
        { Id: null, Title: "None", Name: "None" },
        ...aggregateExpressions,
      ];

      //fields - already sorted by title by api
      //let fields = action.payload.fields.filter(e => (e.IsExpression == false && e.IsAggregate==false && e.DataTypeId == 2)).map(item => ({Id: item.Id, Title: item.Title, Name: item.Name}));
      let fields = action.payload.fields
        .filter((e) => e.IsExpression == false && e.IsAggregate == false)
        .map((item) => ({
          Id: item.Id,
          Title: item.Title,
          Name: item.Name,
          DataTypeId: item.DataTypeId,
        }));
      //fields = sortArrayByString(fields, "Title");
      fields = [
        { Id: null, Title: "None", Name: "None", DataTypeId: 0 },
        ...fields,
      ];

      //time fields - already sorted by title by api
      let timeFields = action.payload.fields
        .filter(
          (e) =>
            e.IsExpression == false &&
            e.IsAggregate == false &&
            e.DataTypeId == 5 &&
            e.IsUTC == true,
        )
        .map((item) => ({ Id: item.Id, Title: item.Title, Name: item.Name }));
      //timeFields = sortArrayByString(timeFields, "Title");
      timeFields = [{ Id: 0, Title: "None", Name: "None" }, ...timeFields];

      //users - already sorted by name by api
      let users = action.payload.users.map((item) => ({
        Id: item.Id,
        Name: item.Name,
        IsActive: item.IsActive,
        Checked: false,
      }));
      //users = sortArrayByString(users, "Name");

      //groups - already sorted by name by api
      let groups = action.payload.groups.map((item) => ({
        Id: item.Id,
        Name: item.Name,
        Checked: false,
      }));

      //groupby fields - fields excluding is expression and excluding is aggregate and those that are not groupable in alert
      let groupByFields = action.payload.fields
        .filter((e) => e.IsAggregate == false && e.IsGroupableInAlert == true)
        .map((item) => ({
          Id: item.Id,
          Title: item.Title,
          Name: item.Name,
          Checked: false,
        }));
      //let groupByFields = action.payload.fields.filter(e => (e.IsExpression == false && e.IsAggregate==false && e.IsGroupableInAlert == true)).map(item => ({Id: item.Id, Title: item.Title, Name: item.Name, Checked: false}));

      //where
      let where = action.payload.where;
      for (let prop in where) {
        //must add name and label (lowercases) to every field
        where[prop].name = where[prop].Name;
        where[prop].label = where[prop].Title;

        if (where[prop].DataTypeId == 1) {
          where[prop].inputType = "bool";
          //where[prop].dataType="bool";
        } else if (where[prop].DataTypeId == 2) {
          where[prop].inputType = "number";
          //where[prop].dataType="number";
        } else if (where[prop].DataTypeId == 3) {
          where[prop].inputType = "string";
          //where[prop].dataType="string";
        } else if (where[prop].DataTypeId == 4 || where[prop].DataTypeId == 5) {
          where[prop].inputType = "date";
        }

        if (where[prop].IsLookup) {
          for (let p in where[prop].values) {
            where[prop].values[p].label = where[prop].values[p].Title;
          }
        }
      }

      return {
        ...state,
        processing: false,
        processingText: "",
        currentDataset: {
          ...currentDataset,
          Fields: [...action.payload.fields],
          Users: [...action.payload.users],
          Groups: [...action.payload.groups],
          Where: where,
          Lookups: {
            AggregateExpressions: aggregateExpressions,
            Fields: fields,
            AggregateFunctions: [
              { Id: 0, Title: "None", Name: "None" },
              ...AggregateFunction.functions
                .sort((a, b) =>
                  a.title < b.title ? -1 : a.title == b.title ? 0 : 1,
                )
                .map((e) => ({
                  Id: e.id,
                  Title: e.title,
                  Name: e.name,
                  Description: e.description,
                  AppliesTo: e.appliesTo,
                })),
            ], //getAggregateFunctions(),
            Operators: getComparisonOperators(),
            AlertImportances: getAlertImportances(),
            AlertRecurrsEveryIntervals: getAlertRecurrsEveryIntervals(),
            AlertCoveredPeriodIntervals: getAlertCoveredPeriodIntervals(),
            AlertVariantCoveredPeriodIntervals:
              getAlertVariantCoveredPeriodIntervals(),
            TimeFields: timeFields,
            Users: users,
            Groups: groups,
            GroupByFields: groupByFields,
          },
        },
        formKey: new Date().toString(),
        formInitialValues: { ...defaultFormInitialValues },
        formValues: {
          ...state.formValues,
          DatasetId: action.payload.datasetId,
          Groups: groups,
          GroupedFields: groupByFields,
          Fields: [],
          // WhereObject: {
          //   ...state.formValues.WhereObject,
          //   rules: [],
          // },
        },
        formServerErrors: [],
      };
    },
  },
});

export default createSchedulePageSlice.reducer;

export const loadPage = (currentUser, t) => async (dispatch, getState) => {
  //start with initial values
  dispatch(setInitialState());

  //undispose page
  dispatch(disposePage(false));

  //show loader
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.loadingPage"),
    }),
  );

  try {
    var response = await api({
      url: "reportingdesign/getreportslist",
      method: "post",
      data: {},
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().createSchedulePage.pageDisposed) return;

    let data = response.data.Data;

    dispatch(pageLoaded({ currentUser: currentUser, Reports: data.Reports }));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const loadReportWidgets = (key, t) => async (dispatch, getState) => {
  //show loader
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.loadingPage"),
    }),
  );
  try {
    const response = await api({
      url: "reportingview/getreportingviewByReferenceKey",
      method: "post",
      data: {
        referenceKey: key,
      },
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().createSchedulePage.pageDisposed) return;
    //it's ok to dispatch here
    let data = response.data.Data;

    //transform the response
    dispatch(setFormValue({ id: "Widgets", value: data.Widgets }));
    dispatch(setFormValue({ id: "Datasets", value: data.Datasets }));
    dispatch(setProcessing({ processing: false, processingText: "" }));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const loadGoogleSheets =
  (connectionId, t) => async (dispatch, getState) => {
    //show loader
    dispatch(
      setProcessing({
        processing: true,
        processingText: t("slices.loadingPage"),
      }),
    );
    try {
      const response = await api({
        url: "google/getgoogledrivefileslist",
        method: "post",
        data: { ConnectionId: connectionId },
      });

      //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
      if (getState().createSchedulePage.pageDisposed) return;
      //it's ok to dispatch here
      let data = response.data.Data;

      //transform the response
      dispatch(setFormValue({ id: "GoogleList", value: data.Files }));
      dispatch(setProcessing({ processing: false, processingText: "" }));

      return Promise.resolve(response);
    } catch (err) {
      //i am here => could be any error included 200 and Succeeded=false
      return Promise.reject(err);
    }
  };

export const loadGoogleSheetId =
  (SpreadSheetId, SheetName, SpreadSheetPath, connectionId, t) =>
  async (dispatch, getState) => {
    //show loader
    dispatch(
      setProcessing({
        processing: true,
        processingText: t("slices.loadingPage"),
      }),
    );
    try {
      const response = await api({
        url: "google/getgooglesheetid",
        method: "post",
        data: {
          SpreadSheetId: SpreadSheetId,
          SheetName: SheetName,
          SpreadSheetPath: SpreadSheetPath,
          ConnectionId: connectionId,
        },
      });

      //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
      if (getState().createSchedulePage.pageDisposed) return;
      //it's ok to dispatch here
      let data = response.data.Data;
      dispatch(setFormValue({ id: "GoogleSheetId", value: data.Id }));
      dispatch(setFormValue({ id: "ColumnNames", value: data.ColumnNames }));

      //transform the response
      dispatch(setProcessing({ processing: false, processingText: "" }));

      return Promise.resolve(response);
    } catch (err) {
      //i am here => could be any error included 200 and Succeeded=false
      return Promise.reject(err);
    }
  };

export const getGoogleAuth = (t) => async (dispatch, getState) => {
  //show loader
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.loadingPage"),
    }),
  );
  try {
    const response = await api({
      url: "google/getgoogleauthurl",
      method: "post",
      data: {},
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().createSchedulePage.pageDisposed) return;

    //it's ok to dispatch here
    const { RedirectUrl } = response.data.Data;

    if (RedirectUrl) {
      window.open(RedirectUrl, "_blank", "noopener,noreferrer");
    }

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const loadDataset = (datasetId, t) => async (dispatch, getState) => {
  //show loading
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.loadingDataset"),
    }),
  );

  if (datasetId == 0) {
    dispatch(
      datasetLoaded({
        datasetId: 0,
        fields: [],
        users: [],
        where: [],
        groups: [],
      }),
    );
    return;
  }

  try {
    var response = await api({
      url: "datacorrection/getdatacorrectionbydatasetid",
      method: "post",
      data: {
        datasetId: datasetId,
      },
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().createSchedulePage.pageDisposed) return;

    let data = response.data.Data;

    dispatch(
      datasetLoaded({
        datasetId: datasetId,
        fields: data.Fields,
        users: data.Users,
        groups: data.SecurityGroups,
        where: data.Where,
      }),
    );

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const getConnectionsList = (userId, t) => async (dispatch, getState) => {
  //show loader
  dispatch(
    setProcessing({
      processing: true,
      processingText: t("slices.loadingPage"),
    }),
  );
  try {
    const response = await api({
      url: "connector/getconnections",
      method: "post",
      data: { userId: userId },
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().createSchedulePage.pageDisposed) return;
    //it's ok to dispatch here
    let data = response.data.Data;

    //transform the response
    dispatch(setFormValue({ id: "Connections", value: data.Connections }));

    dispatch(setProcessing({ processing: false, processingText: "" }));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const createScheduleReport =
  (values, t) => async (dispatch, getState) => {
    // show loading
    dispatch(
      setProcessing({
        processing: true,
        processingText: t("slices.loadingPage"),
      }),
    );

    // map custom fields
    const customFields = values.CustomFields.map((w) => ({
      FieldKey: w.field,
      FieldValue: w.value,
    }));

    // select widgets
    const widgets =
      values.widgetId > 0
        ? values.Widgets.filter((w) => w.Id === values.widgetId)
        : values.Widgets;

    const widgetsIds = widgets.map((w) => w.Id);

    let whereObject = JSON.stringify(values.WhereObject);
    let whereStatement = "";
    let whereParameters = [];

    // generate parameterized SQL
    const formatted = formatQuery(values.WhereObject, "parameterized");

    whereParameters =
      values.WhereObject?.rules?.length > 0
        ? collectParametersFromRules(values.WhereObject.rules)
        : [];

    if (
      (whereParameters != null && whereParameters.length > 0) ||
      (values.WhereObject.rules && values.WhereObject.rules.length > 0)
    ) {
      whereStatement = formatted.sql;
    }

    // prepare final widget payload
    const finalWidgets = widgets.map((w) => ({
      RequestId: "",
      DataSourceId: w.DataSourceId,
      WhereStatement: whereStatement,
      WhereParameters: whereParameters ?? [],
      Parameters: [], // optional: if needed
      WidgetsIds: widgetsIds,
      WhereObject: whereObject,
      MaximumRows: w.Settings.MaximumRows,
    }));

    try {
      const response = await api({
        url: "scheduleexport/createscheduleexport",
        method: "post",
        data: {
          Name: values.Name,
          Description: values.Description,
          TypeId: values.Type,
          SourceId: values.Source,
          ReportId: values.rptId,
          RecursEvery: values.RecurrsEvery,
          ConnectorConfigId: values.ConnectionId,
          GoogleSpreadSheetId: values.GoogleSheetId,
          Cron: values.CronValue,
          StartImmediately: false,
          Widgets: finalWidgets,
          CustomFields: customFields,
        },
      });

      if (getState().createSchedulePage.pageDisposed) return;

      dispatch(setProcessing({ processing: false, processingText: "" }));

      // return response if needed
      return Promise.resolve(response);
    } catch (err) {
      // handle errors
      return Promise.reject(err);
    }
  };

export const {
  setInitialState,
  setProcessing,
  setPageValue,
  setPanes,
  pageLoaded,
  setFormValue,
  createEntity,
  entityLoaded,
  entityInserted,
  entityUpdated,
  entityCloned,
  entityDeleted,
  disposePage,
  setCurrentStepIndex,
  setSteps,
  setStepPages,
  setFieldProperty,
  showHideGoogleSheets,
  setNavigation,
  datasetLoaded,
} = createSchedulePageSlice.actions;
